// composables
import { ref, watch, reactive, nextTick, type Ref } from 'vue'
// utilities
import { alertToast } from '@/utilities/notification'
import { getDictionaryItem } from '@/dictionary'
// composables
import useApiRequest from '@/composables/useApiRequest'
import useVuelidate from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'

/**
 * Composable for managing agreement timeline operations
 * Handles fetching timeline, adding notes, and editing notes
 */
export default function useAgreementTimeline(agreementID: Ref<string | number>) {
  // state
  const timelineData = ref<Array<Record<string, unknown>>>([])
  const recentTimelineData = ref<Array<Record<string, unknown>>>([])
  const editNoteModalIsOpen = ref(false)
  const editNoteHasUnsavedChanges = ref(false)

  const editNoteInitState = reactive({
    id: null as number | null,
    content: null as string | null
  })

  const editNoteState = reactive({
    id: null as number | null,
    content: null as string | null
  })

  // api requests
  const timelineApiRequest = useApiRequest()
  const editNoteApiRequest = useApiRequest()

  // validation
  const editNoteRules = {
    content: { required: helpers.withMessage("Notes can't be empty", required) }
  }

  const editNoteV$ = useVuelidate(editNoteRules, editNoteState)

  /**
   * Fetches timeline data from the API
   */
  function fetchTimeline(): void {
    timelineApiRequest.send({ endpoint: '/v1/agreements/' + agreementID.value + '/timeline' }).then(response => {
      if (!Array.isArray(response.data)) {
        return
      }
      // Loop through each timeline item and map the timeline type from enums
      response.data.forEach((item: Record<string, unknown>) => {
        const itemType = getDictionaryItem('agreementTimelineTypes', item.type as string)

        if (itemType && itemType.key !== 'ERROR') {
          item.timelineType = itemType

          if (item.type == 'appointment_booked') {
            item.subjectLink = `/agreement/${agreementID.value}/appointments/${item.resourceId}`
          }
        } else {
          item.timelineType = { icon: 'far fa-question-circle', inMessageBubble: false }
        }
      })
      timelineData.value = response.data
      recentTimelineData.value = response.data.slice(0, 5)
    })
  }

  /**
   * Handles when a new note is added via AgreementTimeline component
   * Updates the recent timeline data to include the new note
   * @param newNoteItem - The new note item to add
   */
  function onNoteAdded(newNoteItem: Record<string, unknown>): void {
    // Update recent timeline data - add to beginning if not already present
    if (
      recentTimelineData.value &&
      !recentTimelineData.value.find(item => item.resourceId === newNoteItem.resourceId)
    ) {
      recentTimelineData.value.unshift(newNoteItem)
      // Keep only the first 5 items
      if (recentTimelineData.value.length > 5) {
        recentTimelineData.value = recentTimelineData.value.slice(0, 5)
      }
    }
  }

  /**
   * Opens the edit note modal
   * @param item - The timeline item to edit
   */
  function editNote(item: { resourceId: number; text: string }): void {
    editNoteInitState.id = item.resourceId
    editNoteInitState.content = item.text

    editNoteState.id = item.resourceId
    editNoteState.content = item.text

    editNoteModalIsOpen.value = true

    // Reset validation state when opening
    nextTick(() => {
      if (editNoteV$.value) {
        editNoteV$.value.$reset()
      }
    })
  }

  /**
   * Submits the edited note
   */
  async function submitEditNote(): Promise<void> {
    // Manual validation since Vuelidate seems to have conflicts with reactive objects
    if (!editNoteState.id) {
      alertToast('Validation failed', 'Note ID is missing', 'error')
      return
    }

    if (!editNoteState.content || editNoteState.content.trim() === '') {
      alertToast('Validation failed', "Notes can't be empty", 'error')
      return
    }

    // Validate with Vuelidate for UI error display, but don't block on it
    await editNoteV$.value.$validate()

    editNoteApiRequest
      .send({
        endpoint: '/v1/agreements/' + agreementID.value + '/notes/' + editNoteState.id,
        method: 'PATCH',
        data: JSON.stringify({
          content: editNoteState.content
        })
      })
      .then(response => {
        if (!response.success) {
          alertToast('Failed to update note', response.message, 'error')
          return
        }

        const noteToUpdate = timelineData.value.find(item => item.resourceId == editNoteState.id)
        if (noteToUpdate) {
          noteToUpdate.text = editNoteState.content
        }

        editNoteState.content = null
        editNoteInitState.content = null

        editNoteModalIsOpen.value = false

        alertToast('Note Updated', null, 'success')
      })
      .catch(error => {
        console.error('Error updating note:', error)
        alertToast('Failed to update note', (error as Error).message, 'error')
      })
  }

  /**
   * Watches for changes in edit note state to track unsaved changes
   */
  watch(
    () => editNoteState.content,
    () => {
      if (editNoteState.content != editNoteInitState.content) {
        editNoteHasUnsavedChanges.value = true
      } else {
        editNoteHasUnsavedChanges.value = false
      }
    },
    { deep: true }
  )

  return {
    // state
    timelineData,
    recentTimelineData,
    editNoteModalIsOpen,
    editNoteHasUnsavedChanges,
    editNoteState,
    editNoteV$,
    editNoteApiRequest,
    // methods
    fetchTimeline,
    onNoteAdded,
    editNote,
    submitEditNote
  }
}
